import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";

const CIVILITIES = ["Monsieur", "Madame", "Autre"];
const AVAIL = ["Le matin", "Entre midi et deux", "L'après-midi", "En fin de journée", "À tout moment"];
const PROJECT_TYPES = [
  "Logo", "Identité visuelle", "Brochure / Plaquette", "Flyer / Affiche",
  "Livre / Édition", "Site internet", "Packaging", "Illustration", "Autre",
];
const HOW_HEARD = [
  "Google / moteur de recherche", "Bouche à oreille / recommandation",
  "Réseaux sociaux", "Déjà client", "Autre",
];

// Validation d'un numéro de téléphone français (fixe ou mobile, +33 ou 0)
const FR_PHONE = /^(?:(?:\+|00)33[ .\-]?(?:\(0\)[ .\-]?)?|0)[1-9](?:[ .\-]?\d{2}){4}$/;
const isValidFrPhone = (v) => FR_PHONE.test((v || "").trim());

const STEPS = ["Vous", "Contact", "Projet"];

export default function LeadForm({ compact = false, withMessage = false }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    civility: "Monsieur", firstname: "", name: "", phone: "", email: "",
    availability: "À tout moment", project_type: "", deadline: "", how_heard: "", project: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = (s) => {
    if (s === 0 && !form.name.trim()) {
      toast.error("Merci d'indiquer votre nom.");
      return false;
    }
    if (s === 1) {
      if (!form.phone.trim()) { toast.error("Le numéro de téléphone est obligatoire."); return false; }
      if (!isValidFrPhone(form.phone)) { toast.error("Numéro de téléphone français invalide (ex : 06 12 34 56 78)."); return false; }
    }
    return true;
  };

  const next = () => { if (validateStep(step)) setStep((s) => Math.min(s + 1, 2)); };
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (e) => {
    e.preventDefault();
    // Guard: Enter key or early submit only advances until the last step
    if (step < 2) { next(); return; }
    if (!validateStep(0) || !validateStep(1)) return;
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Demande envoyée ! Julien vous recontacte très vite.");
      setForm({ civility: "Monsieur", firstname: "", name: "", phone: "", email: "", availability: "À tout moment", project_type: "", deadline: "", how_heard: "", project: "", message: "" });
      setStep(0);
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-zinc-900/80 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder-zinc-500 focus:border-yellow-400 focus:outline-none transition-colors";

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-5">
      {/* Progress steps */}
      <div className="flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex items-center gap-2">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold shrink-0 transition-colors ${
              i <= step ? "bg-yellow-400 text-black" : "bg-zinc-800 text-zinc-500"
            }`}>
              {i < step ? <Check className="w-4 h-4" /> : i + 1}
            </div>
            <span className={`text-xs font-medium hidden sm:block ${i === step ? "text-yellow-400" : "text-zinc-500"}`}>{label}</span>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px ${i < step ? "bg-yellow-400" : "bg-zinc-800"}`} />}
          </div>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.25 }}
          className="space-y-4"
        >
          {step === 0 && (
            <>
              <select data-testid="lead-civility" value={form.civility} onChange={(e) => set("civility", e.target.value)} className={inputCls}>
                {CIVILITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="lead-firstname" value={form.firstname} onChange={(e) => set("firstname", e.target.value)} placeholder="Votre prénom" className={inputCls} />
              <input data-testid="lead-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Votre nom *" className={inputCls} />
            </>
          )}
          {step === 1 && (
            <>
              <input data-testid="lead-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Votre N° de téléphone * (ex : 06 12 34 56 78)" className={inputCls} />
              <input data-testid="lead-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="E-mail" className={inputCls} />
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Meilleur moment pour vous rappeler</label>
                <select data-testid="lead-availability" value={form.availability} onChange={(e) => set("availability", e.target.value)} className={inputCls}>
                  {AVAIL.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Type de projet</label>
                <select data-testid="lead-project-type" value={form.project_type} onChange={(e) => set("project_type", e.target.value)} className={inputCls}>
                  <option value="">— Sélectionnez —</option>
                  {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Date de livraison souhaitée</label>
                <input data-testid="lead-deadline" type="date" value={form.deadline} onChange={(e) => set("deadline", e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1.5">Comment avez-vous connu Black Lemon Studio ?</label>
                <select data-testid="lead-how-heard" value={form.how_heard} onChange={(e) => set("how_heard", e.target.value)} className={inputCls}>
                  <option value="">— Sélectionnez —</option>
                  {HOW_HEARD.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <textarea data-testid="lead-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Décrivez votre projet (optionnel)…" rows={withMessage ? 4 : 2} className={inputCls} />
            </>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center gap-3">
        {step > 0 && (
          <button type="button" data-testid="lead-prev" onClick={prev} className="flex items-center gap-2 px-4 py-3 rounded-md border border-white/10 text-sm text-zinc-300 hover:bg-white/5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>
        )}
        {step < 2 ? (
          <button type="button" data-testid="lead-next" onClick={next} className="flex-1 flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold py-3.5 rounded-md hover:bg-yellow-300 transition-colors">
            Suivant <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button data-testid="lead-submit" type="submit" disabled={loading} className="flex-1 flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold py-3.5 rounded-md hover:bg-yellow-300 disabled:opacity-60 transition-colors">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
            Envoyer ma demande
          </button>
        )}
      </div>
    </form>
  );
}
